import { api } from "@convex/_generated/api";
import type { Id } from "@convex/_generated/dataModel";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  createFileRoute,
  Outlet,
  useNavigate,
  useParams,
  useSearch,
} from "@tanstack/react-router";
import { useQuery as useConvexQuery } from "convex/react";

import { toConvexTableId } from "~/lib/convex-table-id";

// Avoids TS2589 from api.d.ts deep type chain
const taxCategoriesRemoveRef = api.taxCategories.remove;

const taxCategoriesAddItemsRef = api.taxCategories.addItems;

const getPreDeleteDataRef = api.taxCategories.getPreDeleteData;

const getAllTaxCategoriesRef = api.taxCategories.getAll;

/** Matches return type of taxCategories.getPreDeleteData */
type MenuItemMapping = Array<{
  menuId: string;
  menuName: string;
  items: Array<{ itemId: string; itemName: string; itemPrice: string }>;
}>;

import { Info } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

import { LoadingPanel } from "~/components/shared/loading-panel";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "~/components/ui/accordion";
import { Button } from "~/components/ui/button";
import { Combobox } from "~/components/ui/combobox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Label } from "~/components/ui/label";
import { convexClient } from "~/integrations/convex/provider";
import {
  optionalSearchString,
  requiredSearchString,
} from "~/lib/parse-enum-value";
import { ProductEvents } from "~/lib/posthog";

// V4.1: Route-based modal for URL context tracking
export const Route = createFileRoute(
  "/_authenticated/settings/tax-categories/$id/delete"
)({
  component: TaxCategoryDeleteModal,
  validateSearch: (
    search: Record<string, unknown>
  ): { name: string; value: string; newTaxCategoryId?: string } => ({
    name: requiredSearchString(search.name),
    value: requiredSearchString(search.value, "0"),
    newTaxCategoryId: optionalSearchString(search.newTaxCategoryId),
  }),
});

function TaxCategoryDeleteModal() {
  const navigate = useNavigate();
  const { id } = useParams({
    from: "/_authenticated/settings/tax-categories/$id/delete",
  });
  const { name, value, newTaxCategoryId } = useSearch({
    from: "/_authenticated/settings/tax-categories/$id/delete",
  });

  const handleClose = () => {
    void navigate({ to: "/settings/tax-categories" });
  };

  // V4.1: Navigation callback for inline category creation
  const handleCreateTaxCategory = (categoryName: string) => {
    void navigate({
      to: `/settings/tax-categories/${id}/delete/create-category`,
      search: {
        name: categoryName,
        deletingId: id,
        deletingName: name,
        deletingValue: value,
      },
    });
  };

  const handleClearNewCategoryId = () => {
    void navigate({
      to: `/settings/tax-categories/${id}/delete`,
      search: { name, value },
      replace: true,
    });
  };

  return (
    <>
      <Dialog onOpenChange={(open) => !open && handleClose()} open>
        <TaxCategoriesDeleteModalContent
          newTaxCategoryId={newTaxCategoryId}
          onClearNewCategoryId={handleClearNewCategoryId}
          onClose={handleClose}
          onCreateTaxCategory={handleCreateTaxCategory}
          taxCategory={{ id, name, value }}
        />
      </Dialog>
      {/* V4.1: Outlet for child routes (e.g., /create-category modal) */}
      <Outlet />
    </>
  );
}

function TaxCategoriesDeleteModalContent({
  onClose,
  taxCategory,
  newTaxCategoryId,
  onCreateTaxCategory,
  onClearNewCategoryId,
}: {
  taxCategory: {
    id: string;
    name: string;
    value: string;
  };
  onClose: () => void;
  // V4.1: Route-based category creation props
  newTaxCategoryId?: string;
  onCreateTaxCategory: (name: string) => void;
  onClearNewCategoryId: () => void;
}) {
  const [showReplaceModal, setShowReplaceModal] = useState(false);
  const queryClient = useQueryClient();

  const taxCategoryDeleteMutation = useMutation({
    mutationFn: ({ id }: { id: Id<"taxCategories"> }) => {
      return convexClient.mutation(taxCategoriesRemoveRef, { id });
    },
    onSuccess: () => {
      ProductEvents.taxCategoryDeleted(taxCategory.id);
      void queryClient.invalidateQueries({ queryKey: ["taxCategories"] });
      void queryClient.invalidateQueries({ queryKey: ["items"] });
    },
  });

  const taxCategoryDelete = async (id: string) => {
    try {
      await taxCategoryDeleteMutation.mutateAsync({
        id: toConvexTableId("taxCategories", id),
      });
      toast.success("Tax Category deleted successfully");
      if (itemIds.length > 0) {
        setShowReplaceModal(true);
      } else {
        onClose();
      }
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Failed to delete tax category"
      );
    }
  };

  const menus = useConvexQuery(getPreDeleteDataRef, {
    id: toConvexTableId("taxCategories", taxCategory.id),
  });

  const itemIds = useMemo(() => {
    if (!menus) {
      return [];
    }
    const idSet = new Set<string>();
    for (const menu of menus) {
      for (const item of menu.items) {
        idSet.add(item.itemId);
      }
    }
    return Array.from(idSet);
  }, [menus]);

  if (showReplaceModal && menus) {
    return (
      <TaxCategoriesReplaceModalContent
        itemIds={itemIds}
        menus={menus}
        newTaxCategoryId={newTaxCategoryId}
        onClearNewCategoryId={onClearNewCategoryId}
        onCreateTaxCategory={onCreateTaxCategory}
        onModalClose={onClose}
      />
    );
  }

  return (
    <DialogContent
      className="w-full max-w-120"
      noCloseButton
      noScrollPrevention
    >
      <DialogHeader className="items-center space-y-0">
        <DialogTitle className="text-foreground">
          Delete Tax Category
        </DialogTitle>
        <DialogDescription className="body text-muted-foreground text-center">
          Are you sure you want to delete this tax category? All items using
          this tax category will be untaxed.
        </DialogDescription>
      </DialogHeader>
      <div className="bg-muted text-foreground flex rounded-lg p-4">
        <p>
          {taxCategory.name} {Number.parseFloat(taxCategory.value).toString()}%
        </p>
      </div>
      <AffectedItems itemsNumber={itemIds.length} menus={menus} />
      <div className="flex w-full justify-between space-x-3">
        <Button
          className="w-full"
          onClick={async () => {
            onClose();
          }}
          size="lg"
          type="button"
          variant="outline"
        >
          Cancel
        </Button>
        <Button
          className="w-full"
          disabled={taxCategoryDeleteMutation.isPending}
          onClick={async () => {
            await taxCategoryDelete(taxCategory.id);
          }}
          size="lg"
          type="button"
          variant="destructive"
        >
          Delete
        </Button>
      </div>
    </DialogContent>
  );
}

const TaxCategoriesReplaceModalContent = ({
  itemIds,
  menus,
  onModalClose,
  newTaxCategoryId,
  onCreateTaxCategory,
  onClearNewCategoryId,
}: {
  itemIds: string[];
  menus: MenuItemMapping;
  onModalClose: () => void;
  newTaxCategoryId?: string;
  onCreateTaxCategory: (name: string) => void;
  onClearNewCategoryId: () => void;
}) => {
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const taxCategories = useConvexQuery(getAllTaxCategoriesRef);

  // V4.1: Handle newly created category from child route
  useEffect(() => {
    if (newTaxCategoryId) {
      setSelectedCategory(newTaxCategoryId);
      onClearNewCategoryId();
    }
  }, [newTaxCategoryId, onClearNewCategoryId]);

  const addItemsToCategoryMutation = useMutation({
    mutationFn: (data: {
      categoryId: Id<"taxCategories">;
      items: Id<"items">[];
    }) => {
      return convexClient.mutation(taxCategoriesAddItemsRef, data);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["taxCategories"] });
      await queryClient.invalidateQueries({ queryKey: ["items"] });
      toast.success("The items were successfully updated");
      onModalClose();
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : "Failed to update items");
    },
  });

  const addItemsToCategory = async (data: {
    categoryId: Id<"taxCategories">;
    items: Id<"items">[];
  }) => {
    setError(null);
    await addItemsToCategoryMutation.mutateAsync(data);
  };

  if (!taxCategories) {
    return <LoadingPanel message="Loading tax categories..." />;
  }

  return (
    <DialogContent
      className="w-full max-w-120"
      noCloseButton
      noScrollPrevention
    >
      <DialogHeader className="items-center space-y-0">
        <DialogTitle className="text-foreground">
          Replace Tax Category
        </DialogTitle>
        <DialogDescription className="body text-muted-foreground text-center">
          Do you want to apply another tax category to affected items?
        </DialogDescription>
      </DialogHeader>
      <div className="space-y-2">
        <Label>New Tax Category for Items</Label>
        <Combobox
          createNewOption={{
            label: "Create new category",
            onClick: onCreateTaxCategory,
          }}
          dropdownClassName="w-96"
          items={taxCategories.map((c) => ({
            label: c.name,
            value: c._id,
          }))}
          onValueChange={setSelectedCategory}
          value={selectedCategory}
        />
        {error && (
          <p className="body-sm text-destructive dark:text-destructive flex gap-2 leading-tight">
            <Info className="text-destructive size-4" />
            {error}
          </p>
        )}
      </div>
      <AffectedItems itemsNumber={itemIds.length} menus={menus} />
      <div className="flex w-full justify-between space-x-3">
        <Button
          className="w-full"
          onClick={onModalClose}
          size="lg"
          type="button"
          variant="outline"
        >
          Cancel
        </Button>
        <Button
          className="w-full"
          disabled={addItemsToCategoryMutation.isPending}
          onClick={async () => {
            if (!selectedCategory) {
              return setError(
                "Select a new tax category to assign to affected items"
              );
            }
            await addItemsToCategory({
              categoryId: toConvexTableId("taxCategories", selectedCategory),
              items: itemIds.map((id) => toConvexTableId("items", id)),
            });
          }}
          size="lg"
          type="button"
        >
          Save
        </Button>
      </div>
    </DialogContent>
  );
};

const AffectedItems = ({
  menus,
  itemsNumber,
}: {
  menus?: MenuItemMapping;
  itemsNumber: number;
}) => {
  if (itemsNumber === 0 || !menus) {
    return null;
  }

  return (
    <Accordion type="multiple">
      <AccordionItem className="border-b-0" value="menu-items">
        <AccordionTrigger className="subtitle-sm text-foreground/70 leading-tight hover:no-underline">
          Affected items ({itemsNumber})
        </AccordionTrigger>
        <AccordionContent className="space-y-3">
          {menus.map((menu) => (
            <div key={menu.menuId}>
              <p className="body-sm text-foreground/70 leading-tight">
                {menu.menuName}
              </p>
              <div className="mx-2 mt-2 space-y-1">
                {menu.items.map((item) => (
                  <p
                    className="body-sm text-muted-foreground leading-tight"
                    key={item.itemId}
                  >
                    {item.itemName}{" "}
                    {Number.parseFloat(item.itemPrice).toFixed(2)} %
                  </p>
                ))}
              </div>
            </div>
          ))}
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
};
